import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import ModeBanner from "../components/ModeBanner";
import ControlPanel from "../components/ControlPanel";
import SettingsView from "../views/SettingsView";
import LogsView from "../views/LogsView";
import V2MetricsBar from "../components/V2MetricsBar";
import V2PanelView from "../views/V2PanelView";
import V2ScannerView from "../views/V2ScannerView";
import V2PositionsView, { V2HistoryView } from "../views/V2PositionsView";
import V2ReportsView from "../views/V2ReportsView";
import { V3RsiTable } from "../components/V3RsiTable";
import { V3OpenRsiPanel } from "../components/V3OpenRsiPanel";
import GuardView from "../views/GuardView";
import { AppSwitcher } from "../components/AppSwitcher";
import { InstallButton } from "../components/InstallButton";
import { BottomNav } from "../components/BottomNav";
import { Activity, LogOut, Gauge, Search, Layers, History, Settings, ScrollText, BarChart3, Radar, Shield } from "lucide-react";

const DOT = { running: "#10B981", paused: "#F59E0B", emergency_stopped: "#EF4444", stopped: "#64748B" };

const TABS = [
  { id: "panel", label: "Panel", icon: Gauge },
  { id: "scanner", label: "Tarayıcı", icon: Search },
  { id: "rsi", label: "RSI Tablosu", icon: Radar },
  { id: "positions", label: "Pozisyonlar", icon: Layers },
  { id: "guard", label: "Korumalı Bot", icon: Shield },
  { id: "history", label: "Geçmiş", icon: History },
  { id: "reports", label: "Raporlar", icon: BarChart3 },
  { id: "settings", label: "Ayarlar", icon: Settings },
  { id: "logs", label: "Günlük", icon: ScrollText },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [rawStatus, setStatus] = useState(null);
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((x) => x.id === t) ? t : "panel";
  });
  const view = "v3";
  const status = rawStatus ? { ...rawStatus, view } : null;
  const st = rawStatus?.v3_bot_status || "stopped";

  const loadStatus = useCallback(() => {
    api.get("/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadStatus();
    const i = setInterval(loadStatus, 5000);
    return () => clearInterval(i);
  }, [loadStatus]);

  return (
    <div className="min-h-screen bg-[#0B0E17] pb-24 sm:pb-10">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0E1320]/90 border-b border-[#1E2738]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-[#3B82F6]/15 border border-[#3B82F6]/30 flex items-center justify-center">
              <Activity className="h-5 w-5 text-[#3B82F6]" />
            </div>
            <AppSwitcher current="futures" subtitle={<span className="text-[10px] text-[#64748B] flex items-center gap-1.5">Demo borsası · <span className="h-1.5 w-1.5 rounded-full inline-block" style={{ background: DOT[st] }} />{st === "running" ? "çalışıyor" : st === "paused" ? "girişler durduruldu" : "durdu"}</span>} />
          </div>
          <div className="flex items-center gap-3">
            <InstallButton />
            {status && <div className="hidden sm:block"><ModeBanner v2 /></div>}
            <button data-testid="logout-button" onClick={logout} className="flex items-center gap-1.5 text-[#64748B] hover:text-[#EF4444] text-xs font-semibold transition-colors">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Çıkış</span>
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-2 overflow-x-auto hidden sm:block">
          <div className="flex gap-1 min-w-max">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#3B82F6] text-[#3B82F6]" : "border-transparent text-[#64748B] hover:text-[#94A3B8]"}`}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {status?.readonly && (
          <div data-testid="readonly-banner" className="bg-[#EF4444]/10 border border-[#EF4444]/50 rounded-lg px-4 py-3 text-sm">
            <div className="font-bold text-[#EF4444]">⚠️ Bu sunucu ({window.location.host}) salt-okunur TEST/ÖNİZLEME ortamıdır — bot burada işlem AÇMAZ.</div>
            <div className="text-xs text-[#94A3B8] mt-1">Canlı bot yalnızca <a href="https://rotarsi.com" className="underline text-[#3B82F6]">rotarsi.com</a> adresinde çalışır. Telefon/bilgisayardaki uygulamayı bu adresten yüklediyseniz kaldırıp rotarsi.com'dan yeniden yükleyin; aksi halde iki uygulamadaki veriler farklı görünür.</div>
          </div>
        )}
        <V2MetricsBar status={status} p={view} />
        {(tab === "panel" || tab === "scanner" || tab === "positions") && <ControlPanel status={status} onChange={loadStatus} />}

        {tab === "panel" && <V2PanelView status={status} p={view} />}
        {tab === "scanner" && <V2ScannerView p={view} />}
        {tab === "rsi" && (
          <div className="space-y-4 fade-up" data-testid="rsi-page">
            <div>
              <h2 className="font-head text-xl font-bold flex items-center gap-2"><Radar className="h-5 w-5 text-[#3B82F6]" /> Canlı RSI({status?.v3_rsi_period ?? 7}) Tablosu ({status?.v3_rsi_interval_label ?? "5 dk"} mum) — Rotarsi Al Sat Botu</h2>
              <p className="text-xs text-[#64748B] mt-1">Üstte açık işlemlerin RSI değişimi; altta 24s hacmi eşiğin üzerindeki (varsayılan ≥ 1 milyar USDT) coinlerin güncel {status?.v3_rsi_interval_label ?? "5 dk"} Wilder RSI({status?.v3_rsi_period ?? 7}) değeri (açık işlemi olanlar hariç). Yeşil = LONG bölgesi, kırmızı = SHORT bölgesi, amber = eşiğe ≤ 5 puan. 5 sn'de bir yenilenir.</p>
            </div>
            <V3OpenRsiPanel />
            <V3RsiTable />
          </div>
        )}
        {tab === "positions" && <V2PositionsView p={view} />}
        {tab === "guard" && <GuardView />}
        {tab === "history" && <V2HistoryView p={view} />}
        {tab === "reports" && <V2ReportsView />}
        {tab === "settings" && <SettingsView status={status} onChange={loadStatus} />}
        {tab === "logs" && <LogsView />}
      </main>
      <BottomNav tabs={TABS} tab={tab} setTab={setTab} />
    </div>
  );
}
